import { CirclePause, CirclePlay, CircleStop, PhoneOutgoing } from 'lucide-react'
import { useSearchParams } from 'react-router'

import aircallLogo from '@/assets/aircall.png'
import dialpadLogo from '@/assets/dialpad.png'
import { callerLabel, IncomingCallBanner } from '@/components/IncomingCallBanner'
import { InternalAgentBarButton } from '@/components/InternalAgent/InternalAgentBarButton'
import { PartyLabel } from '@/components/PartyLabel'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useSidebar } from '@/components/ui/sidebar'
import { useSecondsUntil } from '@/hooks/useSecondsUntil'
import { cn } from '@/lib/utils'
import {
  AutomationStatus,
  useCancelAutomation,
  usePauseAutomation,
  useStartAutomation,
} from '@/models/outboundCallAutomation'
import {
  type IncomingCall,
  type OutboundAutomationState,
  TelephonyVendor,
  useCallerLookup,
  useRealtime,
} from '@/models/realtime'
import type { AircallContextValue } from './Aircall/AircallProvider'
import { useAircall } from './Aircall/useAircall'
import { useDialpad } from './Dialpad/useDialpad'

// "Calling…" appears only here — i.e. only when a call to a specific lead is
// actually in flight. Anything else (skipping, between calls) reads "Processing…".
const CALL_RESULT_LABELS: Record<string, string> = {
  CONNECTING: 'Calling…',
  CONNECTED: 'Connected',
  MISSED: 'Missed',
}

interface CommsButtonProps {
  logo: string
  label: string
  active: boolean
  ringing: boolean
  /** Tailwind bg-color class for the status dot; omit for no dot. */
  statusDot?: string
  onClick: () => void
}

function CommsButton({ logo, label, active, ringing, statusDot, onClick }: CommsButtonProps) {
  const a11yLabel = ringing ? `${label} — call ringing` : label
  return (
    <Button
      variant="outline"
      size="icon"
      onClick={onClick}
      aria-label={a11yLabel}
      title={a11yLabel}
      className={cn(
        'relative size-11 border-none p-0',
        active && 'border-primary ring-primary/40 ring-2',
        ringing && 'animate-pulse motion-reduce:animate-none'
      )}
    >
      <img src={logo} alt={label} className="size-8 rounded-md" />
      {ringing ? (
        <span className="absolute -top-1 -right-1 size-3 rounded-full bg-orange-500 ring-2 ring-white" />
      ) : statusDot ? (
        <span className={cn('absolute -top-1 -right-1 size-3 rounded-full ring-2 ring-white', statusDot)} />
      ) : null}
    </Button>
  )
}

// The Aircall workspace is a cross-site iframe that silently blanks when its
// login is blocked (third-party cookies). Show the connection state + a
// first-party "Connect" popup instead of a mystery empty box.
function AircallControls({ ctx }: { ctx: AircallContextValue }) {
  const errored = ctx.status === 'error'
  const needsConnect = errored || ctx.workspaceStalled
  const dot = errored ? 'bg-red-500' : ctx.isLoggedIn ? undefined : 'bg-neutral-300'
  const title = errored
    ? 'Aircall unavailable'
    : ctx.isLoggedIn
      ? 'Aircall — connected'
      : 'Aircall — not connected'

  return (
    <>
      {needsConnect && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={() => ctx.connect()}
          title="Aircall didn't load (usually blocked third-party cookies). Log in via the popup, then reopen."
        >
          Connect Aircall
        </Button>
      )}
      <CommsButton
        logo={aircallLogo}
        label={title}
        active={ctx.expanded}
        ringing={!!ctx.ringingCall}
        statusDot={dot}
        onClick={() => (ctx.expanded ? ctx.collapse() : ctx.expand())}
      />
    </>
  )
}

// Live outbound-call-automation status + controls: who's being dialed, the call
// result, and pause (while running) / resume + end (while paused). A completed
// run shows read-only "Completed" — dismissal happens in the automation dialog.
function AutomationControls({
  state,
  onShowDetails,
}: {
  state: OutboundAutomationState
  onShowDetails: (leadId: string) => void
}) {
  const start = useStartAutomation()
  const pause = usePauseAutomation()
  const cancel = useCancelAutomation()
  const id = state.automationId
  const running = state.status === AutomationStatus.RUNNING
  const paused = state.status === AutomationStatus.PAUSED
  const completed = state.status === AutomationStatus.COMPLETED
  const resultLabel = state.callResult ? CALL_RESULT_LABELS[state.callResult] : null
  // While dialing, show the position of the lead being worked (done + the one in
  // flight) so the last of 2 reads "2/2", not "1/2". Paused/completed show how
  // many are done.
  const position = running ? Math.min(state.done + 1, state.total) : state.done
  const currentLead = state.currentLead
  // Auto-pause between batches: seconds left until it resumes (null when not paused).
  const pausedSecs = useSecondsUntil(state.autoPausedUntil)

  return (
    <div className="flex max-w-[min(60vw,32rem)] min-w-0 items-center gap-2">
      {running && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1 text-xs"
          loading={pause.isPending ? 'Pausing…' : undefined}
          onClick={() => pause.mutate(id)}
        >
          <CirclePause className="size-4" /> Pause
        </Button>
      )}
      {paused && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 gap-1 text-xs"
            loading={start.isPending ? 'Resuming…' : undefined}
            onClick={() => start.mutate(id)}
          >
            <CirclePlay className="size-4" /> Resume
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 gap-1 text-xs"
            loading={cancel.isPending ? 'Ending…' : undefined}
            onClick={() => cancel.mutate(id)}
          >
            <CircleStop className="size-4" /> End
          </Button>
        </>
      )}
      <PhoneOutgoing
        className={cn(
          'size-4 shrink-0',
          running && pausedSecs === null
            ? 'animate-pulse text-green-600 motion-reduce:animate-none'
            : 'text-neutral-400'
        )}
      />
      <span className="truncate text-sm">
        {currentLead ? (
          <>
            <PartyLabel contactName={currentLead.contact} company={currentLead.legalName} />
            <span className="text-muted-foreground">
              {' '}
              · {position}/{state.total}
            </span>
            {resultLabel && <span className="text-muted-foreground"> · {resultLabel}</span>}
          </>
        ) : pausedSecs !== null ? (
          <span className="text-muted-foreground">
            Paused — resuming in {pausedSecs}s · {position}/{state.total}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {paused ? 'Paused' : completed ? 'Completed' : 'Processing…'} · {position}/{state.total}
          </span>
        )}
      </span>
      {currentLead && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 text-xs"
          onClick={() => onShowDetails(currentLead.id)}
        >
          Show details
        </Button>
      )}
    </div>
  )
}

// Fixed 50px bar shown to CAI staff (mounted in InternalLayout). The square logo
// buttons toggle the embedded Aircall / Dialpad dialer widgets — replacing the
// old floating pills.
export function CommsBottomBar() {
  const aircall = useAircall()
  const dialpad = useDialpad()
  const [searchParams, setSearchParams] = useSearchParams()
  // The bar is fixed full-width and sits under the fixed sidebar; pad its left
  // edge by the sidebar's current width so left-pinned content isn't hidden. On
  // mobile the sidebar is an overlay (no persistent offset).
  const { state: sidebarState, isMobile: sidebarIsMobile } = useSidebar()
  const leftInset = sidebarIsMobile
    ? undefined
    : sidebarState === 'collapsed'
      ? '4rem' // 3rem icon rail + 1rem gutter
      : '17rem' // 16rem sidebar + 1rem gutter

  const aircallReady = !!aircall && aircall.status !== 'loading' && aircall.status !== 'disabled'
  // One SSE stream for the whole bar (CAI-admin only, so always subscribe).
  const { incomingCall: sseCall, outboundAutomation } = useRealtime(true)

  // Only an explicitly-inbound widget ring: Dialpad often omits the direction, and
  // an outbound dial rendered as an incoming call is worse than awaiting the webhook.
  const widgetRing =
    aircall?.ringingCall?.direction === 'inbound'
      ? { vendor: TelephonyVendor.AIRCALL, from: aircall.ringingCall.from }
      : dialpad?.ringingCall?.direction === 'inbound'
        ? { vendor: TelephonyVendor.DIALPAD, from: dialpad.ringingCall.from }
        : null
  const baseIncoming: IncomingCall | null =
    sseCall ??
    (widgetRing?.from
      ? {
          vendor: widgetRing.vendor,
          callId: '',
          fromNumber: widgetRing.from,
          lead: null,
          matchCount: 0,
          customer: null,
          customerMatchCount: 0,
        }
      : null)

  // Webhook missing/late, or a widget-only number: re-resolve from the number alone.
  const unmatchedNumber =
    baseIncoming && !baseIncoming.lead && !baseIncoming.customer ? baseIncoming.fromNumber : null
  const { data: lookup } = useCallerLookup(unmatchedNumber)
  const incoming: IncomingCall | null =
    baseIncoming && unmatchedNumber && (lookup?.lead || lookup?.customer)
      ? { ...baseIncoming, ...lookup }
      : baseIncoming

  function showDetails(leadId: string) {
    const next = new URLSearchParams(searchParams)
    next.set('call_lead', leadId)
    setSearchParams(next)
  }

  // Announce the actual banner (SSE- or widget-driven), not just widget ringing.
  const announcement = incoming
    ? `Incoming call from ${callerLabel(incoming)}`
    : dialpad?.ringingCall && dialpad.ringingCall.direction !== 'outbound'
      ? 'Incoming call on Dialpad'
      : ''

  return (
    <div
      data-comms-control
      style={leftInset ? { paddingLeft: leftInset } : undefined}
      className="bg-surface fixed inset-x-0 bottom-0 z-[9] flex h-[50px] items-center justify-end gap-3 border-t px-4 shadow-sm"
    >
      <span role="status" aria-live="assertive" className="sr-only">
        {announcement}
      </span>
      {/* Automation pinned to the left; the incoming call stays clustered on the
          right with the dialer logo buttons. */}
      {outboundAutomation && (
        <div className="mr-auto flex min-w-0 items-center">
          <AutomationControls state={outboundAutomation} onShowDetails={showDetails} />
        </div>
      )}
      {incoming && <IncomingCallBanner call={incoming} onShowLead={showDetails} />}
      {aircallReady && <AircallControls ctx={aircall} />}
      {dialpad && dialpad.status === 'ready' && (
        <CommsButton
          logo={dialpadLogo}
          label="Dialpad"
          active={!!dialpad.expanded}
          ringing={!!dialpad.ringingCall}
          onClick={() => (dialpad.expanded ? dialpad.collapse() : dialpad.expand())}
        />
      )}
      {/* The agent isn't a dialer — the divider keeps it from reading as one. */}
      <Separator orientation="vertical" className="h-6!" />
      <InternalAgentBarButton />
    </div>
  )
}
